import React, { useState } from 'react';
import axios from 'axios';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';

interface NewsArticle {
  title: string;
  link: string;
  snippet: string;
  source: string;
  date: string;
  imageUrl?: string;
  content?: string;
  analysis?: {
    analysis: string;
    timestamp: string;
  };
}

interface NewsResultsProps {
  articles: NewsArticle[];
  onArticleAnalyzed: (article: NewsArticle) => void;
  onStartAnalyzing?: (article: NewsArticle) => void;
  onAnalysisError?: () => void;
}

const formatDate = (dateString: string): string => {
  // Handle empty or invalid dates
  if (!dateString) return '';
  
  // Try parsing different date formats
  let date: Date | null = null;
  
  // Try parsing ISO format
  date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    // Try parsing relative time formats
    if (dateString.toLowerCase().includes('hour') || dateString.toLowerCase().includes('stunde')) {
      return 'Heute';
    }
    if (dateString.toLowerCase().includes('day') || dateString.toLowerCase().includes('tag')) {
      return 'Diese Woche';
    }
    if (dateString.toLowerCase().includes('week') || dateString.toLowerCase().includes('woche')) {
      return 'Diesen Monat';
    }
    return dateString; // Return original string if we can't parse it
  }
  
  // Format the date in German locale
  return date.toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

export const NewsResults: React.FC<NewsResultsProps> = ({ 
  articles, 
  onArticleAnalyzed, 
  onStartAnalyzing, 
  onAnalysisError 
}) => {
  const [analyzingArticle, setAnalyzingArticle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async (article: NewsArticle) => {
    setAnalyzingArticle(article.link);
    setError(null);
    
    // Notify parent component that analysis is starting
    if (onStartAnalyzing) {
      onStartAnalyzing(article);
    }

    try {
      // Use our API client with an increased timeout specifically for this operation
      const response = await api.post('/api/analyze', {
        url: article.link
      }, {
        timeout: 60000 // Increase timeout to 60 seconds for analysis operations
      });

      console.log('Analysis response:', response.data);

      // Extract the analysis content
      let analysisContent = '';
      if (typeof response.data === 'string') {
        analysisContent = response.data;
      } else if (response.data?.data?.analysis) {
        // Handle nested analysis object from API
        analysisContent = typeof response.data.data.analysis === 'string' 
          ? response.data.data.analysis 
          : response.data.data.analysis.analysis || 'Keine Analyse verfügbar';
      } else if (response.data?.analysis) {
        // Handle direct analysis object
        analysisContent = typeof response.data.analysis === 'string'
          ? response.data.analysis
          : response.data.analysis.analysis || 'Keine Analyse verfügbar';
      } else {
        analysisContent = 'Keine Analyse verfügbar';
      }

      // Create the analyzed article with string content
      const analyzedArticle: NewsArticle = {
        ...article,
        analysis: {
          analysis: analysisContent,
          timestamp: new Date().toISOString()
        }
      };

      console.log('Processed article:', analyzedArticle);
      onArticleAnalyzed(analyzedArticle);
      
    } catch (err) {
      if (axios.isAxiosError(err)) {
        console.error('Analysis error:', err.response?.data);
        if (err.code === 'ECONNABORTED') {
          setError('Die Analyse dauert länger als erwartet. Bitte versuchen Sie es später erneut oder wählen Sie einen anderen Artikel aus.');
        } else if (err.response?.status === 503) {
          setError('Der Service ist momentan nicht verfügbar. Bitte versuchen Sie es später erneut.');
        } else if (err.response?.status === 404) {
          setError('API-Endpunkt nicht gefunden.');
        } else {
          setError(`Ein Fehler ist aufgetreten: ${err.response?.data?.message || err.message}`);
        }
      } else {
        setError('Ein unerwarteter Fehler ist aufgetreten');
      }
      console.error(err);
      
      // Notify parent component about the error
      if (onAnalysisError) {
        onAnalysisError();
      }
    } finally {
      setAnalyzingArticle(null);
    }
  };

  if (articles.length === 0) {
    return (
      <div className="text-center text-muted-foreground py-8">
        Keine Artikel gefunden. Passen Sie Ihre Suchbegriffe an.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {articles.map((article) => (
        <div key={article.link} className="bg-card p-6 rounded-lg border">
          <h3 className="text-xl font-semibold mb-2">{article.title}</h3>
          
          <div className="flex items-center text-sm text-muted-foreground mb-4">
            <span className="mr-4">{article.source}</span>
            <span>{formatDate(article.date)}</span>
          </div>

          {article.imageUrl && (
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-48 object-cover rounded-lg mb-4"
            />
          )}

          <p className="text-muted-foreground mb-4">{article.snippet}</p>

          <div className="flex items-center justify-between">
            <a
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80"
            >
              Artikel lesen →
            </a>

            {!article.analysis && (
              <Button
                onClick={() => handleAnalyze(article)}
                disabled={analyzingArticle === article.link}
                variant="secondary"
              >
                {analyzingArticle === article.link ? 'Analysiere...' : 'Analysieren'}
              </Button>
            )}
          </div>

          {article.analysis && (
            <div className="mt-4 bg-muted rounded-lg p-4">
              <h4 className="font-semibold mb-2 text-primary">Projektanalyse:</h4>
              <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                {article.analysis?.analysis || 'Keine Analyse verfügbar'}
              </div>
            </div>
          )}
        </div>
      ))}

      {error && (
        <div className="p-4 text-destructive bg-destructive/10 rounded-lg">
          {error}
        </div>
      )}
    </div>
  );
}; 